/**
 * Defines versioned contracts for company-scoped case tags.
 */
import { z } from "zod";
export const TagColorPresetSchema = z.enum([
    "SLATE",
    "BLUE",
    "TEAL",
    "GREEN",
    "AMBER",
    "ORANGE",
    "ROSE",
    "PURPLE",
]);
export const TagColorPresetHex = Object.freeze({
    AMBER: "#B45309",
    BLUE: "#1D4ED8",
    GREEN: "#15803D",
    ORANGE: "#C2410C",
    PURPLE: "#7E22CE",
    ROSE: "#BE123C",
    SLATE: "#334155",
    TEAL: "#0F766E",
});
/**
 * Accepts a preset token or #RRGGBB and normalizes everything to uppercase hex.
 */
export const TagColorSchema = z
    .string()
    .trim()
    .transform((value, context) => {
    const normalized = value.toUpperCase();
    const preset = TagColorPresetSchema.safeParse(normalized);
    if (preset.success) {
        return TagColorPresetHex[preset.data];
    }
    const hex = normalized.startsWith("#") ? normalized : `#${normalized}`;
    if (!/^#[0-9A-F]{6}$/u.test(hex)) {
        context.addIssue({
            code: "custom",
            message: "Expected a preset color or #RRGGBB value.",
        });
        return z.NEVER;
    }
    return hex;
});
export const TagCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z][A-Z0-9_]{1,39}$/, "Expected a stable uppercase code.");
export const CompanyTagSchema = z
    .object({
    code: TagCodeSchema,
    color: TagColorSchema,
    createdAt: z.iso.datetime({ offset: true }),
    description: z.string().trim().max(1_000).nullable(),
    id: z.uuid(),
    isActive: z.boolean(),
    name: z.string().trim().min(1).max(120),
    updatedAt: z.iso.datetime({ offset: true }),
})
    .strict();
export const CompanyTagListQuerySchema = z
    .object({
    includeInactive: z
        .enum(["true", "false"])
        .default("false")
        .transform((value) => value === "true"),
})
    .strict();
export const CompanyTagListResponseSchema = z
    .object({
    items: z.array(CompanyTagSchema).max(500),
})
    .strict();
export const CreateCompanyTagRequestSchema = z
    .object({
    code: TagCodeSchema,
    color: TagColorSchema,
    description: z.string().trim().max(1_000).nullable().optional(),
    name: z.string().trim().min(1).max(120),
})
    .strict();
export const UpdateCompanyTagRequestSchema = z
    .object({
    color: TagColorSchema,
    description: z.string().trim().max(1_000).nullable(),
    name: z.string().trim().min(1).max(120),
})
    .strict();
//# sourceMappingURL=TagContract.js.map